/**
 * Read `hibro-logs` from the page (见 15 / 18).
 *
 * The SharedWorker is the only writer. A download flushes that writer first,
 * then reads the committed file here — the page never appends, and the bytes
 * are not served by the module-asset Service Worker.
 */
import {
  isHibroLogFileName,
  LOG_FILES_DIRNAME,
  refuseForeignLogFileName,
} from '@hibro/shared';

import { isNotFound } from '../ext-host-web/opfs-tree';

export interface LogFileEntry {
  name: string;
  size: number;
}

/** Daily log files, newest first. An empty directory is an empty list. */
export async function listHibroLogFiles(
  flush: () => Promise<void>,
): Promise<LogFileEntry[]> {
  await flush();
  const root = await navigator.storage.getDirectory();
  let dir: FileSystemDirectoryHandle;
  try {
    dir = await root.getDirectoryHandle(LOG_FILES_DIRNAME);
  } catch (err) {
    if (isNotFound(err)) {
      return [];
    }
    throw err;
  }
  const files: LogFileEntry[] = [];
  for await (const entry of dir.values()) {
    if (entry.kind !== 'file' || !isHibroLogFileName(entry.name)) {
      continue;
    }
    const handle = await dir.getFileHandle(entry.name);
    const file = await handle.getFile();
    files.push({ name: entry.name, size: file.size });
  }
  files.sort((a, b) => b.name.localeCompare(a.name));
  return files;
}

/** Download one daily log file. Names outside `hibro-YYYY-MM-DD.log` throw. */
export async function downloadHibroLogFile(
  name: string,
  flush: () => Promise<void>,
): Promise<void> {
  refuseForeignLogFileName(name);
  await flush();
  const root = await navigator.storage.getDirectory();
  const dir = await root.getDirectoryHandle(LOG_FILES_DIRNAME);
  const handle = await dir.getFileHandle(name);
  const file = await handle.getFile();
  const url = URL.createObjectURL(file);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.rel = 'noopener';
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
